// src/services/auth.js
import { ref, computed, watch } from 'vue'
import { clearPreferredLanguage } from './language'
const API_BASE = import.meta.env.VITE_API_URL
const API_KEY = import.meta.env.VITE_API_KEY

// Create reactive auth state that can be imported in components
export const currentUser = ref(null)
export const authToken = ref(localStorage.getItem('auth_token'))
export const isLoading = ref(false)
export const authError = ref(null)

// Create a computed property for authentication status based on token
export const isAuthenticated = computed(() => !!authToken.value)

// Set token with reactivity
export function setAuthToken(token) {
  if (token) {
    localStorage.setItem('auth_token', token) 
  } else {
    localStorage.removeItem('auth_token')
  }
  authToken.value = token
}

// Watch for token changes to update authentication state
watch(authToken, async (newToken) => {
  if (newToken && !currentUser.value) {
    try {
      // Fetch user data when token changes and we don't have user data
      await getCurrentUser()
    } catch (e) {
      console.error('Failed to fetch user data after token change:', e)
    }
  }
})

export async function register(email, name, role = 'patient', language = 'english') {
  isLoading.value = true
  authError.value = null
  
  try {
    const payload = {
      email,
      full_name: name, 
      role,
      preferred_language: language
    }
        
    const response = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(payload)
    })
    
    // For debugging, let's log the raw response
    const responseText = await response.text()
    
    let data
    try {
      data = JSON.parse(responseText)
    } catch (e) {
      console.error("Failed to parse response as JSON:", e)
      throw new Error(`Server returned invalid JSON: ${responseText}`)
    }
    
    if (!response.ok) {
      throw new Error(data.error || 'Registration failed')
    }
    
    // Return the complete data from the server, not just success/message
    return data
  } catch (error) {
    authError.value = error.message
    throw error
  } finally {
    isLoading.value = false
  }
}

export async function login(email, language) {
  try {
    isLoading.value = true
    authError.value = null
        
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      },
      body: JSON.stringify({ email, language })
    })
    
    // For debugging
    const rawText = await response.text()
    
    // Try to parse the response
    let data
    try {
      data = JSON.parse(rawText)
    } catch (parseError) {
      console.error('JSON parse error:', parseError)
      throw new Error('Invalid server response')
    }
    
    // Check for error response
    if (!response.ok) {
      throw new Error(data.error || `HTTP error: ${response.status}`)
    }
        
    // Check if the response has the expected fields
    if (!data.user_id) {
      console.error('Missing user_id in response:', data)
      throw new Error('Server response missing required data')
    }
    
    return data
  } catch (error) {
    console.error('Login error:', error)
    authError.value = error.message || 'Login failed'
    throw error
  } finally {
    isLoading.value = false
  }
}

export async function verify(userId, code, validationType) {
  try {
    isLoading.value = true
    authError.value = null
        
    const response = await fetch(`${API_BASE}/api/auth/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      },
      body: JSON.stringify({
        user_id: userId,
        code: code,
        validation_type: validationType
      })
    })
    
    // For debugging
    const rawText = await response.text()    
    // Try to parse the response
    let data
    try {
      data = JSON.parse(rawText)
    } catch (parseError) {
      console.error('JSON parse error:', parseError)
      throw new Error('Invalid server response')
    }
    
    // Check for error response
    if (!response.ok) {
      throw new Error(data.error || `HTTP error: ${response.status}`)
    }
        
    // Store the token if it exists - use the reactive setter
    if (data.token) {
      setAuthToken(data.token)
      
      // Immediately fetch user data after verification
      try {
        await getCurrentUser()
      } catch (e) {
        console.error('Failed to fetch user data after verification:', e)
      }
    }
    
    return data
  } catch (error) {
    console.error('Verification error:', error)
    authError.value = error.message || 'Verification failed'
    throw error
  } finally {
    isLoading.value = false
  }
}

export async function resendVerification(userId) {
  try {
    isLoading.value = true
    authError.value = null
        
    const response = await fetch(`${API_BASE}/api/auth/resend-verification/${userId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    })
    
    // For debugging
    const rawText = await response.text()
    
    // Try to parse the response
    let data
    try {
      data = JSON.parse(rawText)
    } catch (parseError) {
      console.error('JSON parse error:', parseError)
      throw new Error('Invalid server response')
    }
    
    // Check for error response
    if (!response.ok) {
      throw new Error(data.error || `HTTP error: ${response.status}`)
    }
        
    return data
  } catch (error) {
    console.error('Resend error:', error)
    authError.value = error.message || 'Failed to resend verification code'
    throw error
  } finally {
    isLoading.value = false
  }
}

export async function updateLanguage(token, preferredLanguage) {
  isLoading.value = true
  let error = null
  
  try {
    const payload = {
      preferred_language: preferredLanguage
    }
            

    console.log("token API_KEY", token, API_KEY)
    
    const response = await fetch(`${API_BASE}/api/auth/language`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(payload)
    })
    
    // For debugging, let's log the raw response
    const responseText = await response.text()

    console.log("responseText ", responseText)
    
    let data
    try {
      data = JSON.parse(responseText)
    } catch (e) {
      console.error("Failed to parse response as JSON:", e)
      throw new Error(`Server returned invalid JSON: ${responseText}`)
    }
    
    if (!response.ok) {
      throw new Error(data.error || 'Language update failed')
    }
    
    // Return the complete data from the server
    return data
  } catch (error) {
    console.error("Language update error:", error.message)
    throw error
  } finally {
    isLoading.value = false
  }
}

export async function getCurrentUser() {
  if (!isAuthenticated.value) {
    return null;
  }
  
  try {
    isLoading.value = true;
    
    // Check localStorage first
    const cachedUserData = localStorage.getItem('user_data');
    const cacheTimestamp = localStorage.getItem('user_data_timestamp');
    const now = Date.now();
    
    // Use cache if it exists and is less than 15 minutes old
    if (cachedUserData && cacheTimestamp && (now - parseInt(cacheTimestamp) < 15 * 60 * 1000)) {
      try {
        const userData = JSON.parse(cachedUserData);
        currentUser.value = userData;
        return userData;
      } catch (parseError) {
        console.error('Error parsing cached user data:', parseError);
        // If parsing fails, continue to fetch from server
      }
    }
    
    // Fetch fresh data from server
    const response = await fetch(`${API_BASE}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${authToken.value}`,
        'X-API-Key': API_KEY
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to get user info');
    }
    
    const userData = await response.json();
    currentUser.value = userData;
    
    // Cache the result
    localStorage.setItem('user_data', JSON.stringify(userData));
    localStorage.setItem('user_data_timestamp', now.toString());
    
    return userData;
  } catch (error) {
    console.error('Error getting current user:', error);
    // Clear cache on error
    localStorage.removeItem('user_data');
    localStorage.removeItem('user_data_timestamp');
    logout('invalid_token');
    return null;
  } finally {
    isLoading.value = false;
  }
}

export function logout(reason = 'manual') {
  // Clear auth token
  setAuthToken(null) // Use the reactive setter
  
  // Clear user's preferred language
  clearPreferredLanguage(null)
  
  // Clear user data cache
  localStorage.removeItem('user_data')
  localStorage.removeItem('user_data_timestamp')
  localStorage.removeItem('user_data_id')
  localStorage.removeItem('user_role')
  localStorage.removeItem('user_language')
  
  // Reset current user state
  currentUser.value = null
}

// HTTP client with auth headers
export async function authFetch(url, options = {}) {
  if (!authToken.value) {
    throw new Error('No authentication token available')
  }
  
  const headers = {
    ...options.headers || {},
    'Authorization': `Bearer ${authToken.value}`,
        'X-API-Key': API_KEY
  }
  
  const response = await fetch(url, {
    ...options,
    headers
  })
  
  if (response.status === 401) {
    // Token expired or invalid
    logout('unauthorized')
    window.location.href = '/login'
    throw new Error('Your session has expired. Please login again.')
  }
  
  return response
}

export async function hasProviderRole() {
  // If we don't have user data yet but have a token, fetch it
  if (!currentUser.value && authToken.value) {
    try {
      await getCurrentUser();
    } catch (error) {
      console.error('Error fetching user data for role check:', error);
      return false;
    }
  }
  
  return currentUser.value?.role === 'provider';
}

export const isPatient = computed(() => {
  return currentUser.value?.role === 'patient';
});

export const isProvider = computed(() => {
  return currentUser.value?.role === 'provider';
});

export const isValidator = computed(() => {
  return currentUser.value?.role === 'validator';
});

export const isAdmin = computed(() => {
  return currentUser.value?.role === 'admin';
});

// Initialize user data from localStorage immediately (synchronous)
function initializeAuthFromStorage() {
  const token = localStorage.getItem('auth_token');
  const cachedUserData = localStorage.getItem('user_data');
  const userRole = localStorage.getItem('user_role');
  
  if (token) {
    authToken.value = token;
    
    // Try to load cached user data immediately
    if (cachedUserData) {
      try {
        currentUser.value = JSON.parse(cachedUserData);
      } catch (e) {
        console.error('Error parsing cached user data:', e);
      }
    } else if (userRole) {
      // Fallback: if we have role but no full user data, create minimal user object
      currentUser.value = { role: userRole };
    }
  }
}

// Call initialization immediately (synchronous)
initializeAuthFromStorage();

// Initialize - try to load user data if we have a token
if (authToken.value) {
  getCurrentUser().catch(error => {
    console.error('Failed to fetch initial user data:', error)
  })
}